import {
    Alert,
    Avatar,
    Button,
    Popconfirm,
    Skeleton,
    Space,
    Table,
    Tag,
    Typography,
    message
} from "antd";
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    MinusCircleOutlined
} from "@ant-design/icons";
import {memo, useEffect, useState, useSyncExternalStore} from "react";
import moment from "moment";
import {UserDataDtoGenderEnum} from "acc-rest-client";
import accountRestService from "../../services/acc-rest.service";
import {getCustomUnicodeFlagIcon, getUserStatusLabel} from "../../utils/labelHelper";
import gender_male2_icon from "../../assets/images/gender_male2_icon.png";
import gender_female_icon from "../../assets/images/gender_female_icon.png";
import gender_unknown_icon from "../../assets/images/gender_uknown_icon.png";

const {Title} = Typography;

// todo this page was written by AI I dont know how it work. Next time we should rewite by understanding how it works.
const cache = new Map(); // userId -> {loading, error, seed, neighbours}
const subscribers = new Set();

const subscribe = (onStoreChange) => {
    subscribers.add(onStoreChange);
    return () => subscribers.delete(onStoreChange);
};

const notify = () => subscribers.forEach((onStoreChange) => onStoreChange());

const writeEntry = (userId, entry) => {
    cache.set(userId, entry);
    notify();
    return entry;
};

export const fetchCluster = async (userId) => {
    writeEntry(userId, {loading: true, error: null, seed: null, neighbours: []});
    const [seedResult, duplicatesResult] = await Promise.allSettled([
        accountRestService.getUserData(userId),
        accountRestService.getDuplicateAccounts(userId)
    ]);

    const seed = seedResult.status === "fulfilled" ? seedResult.value : null;

    if (duplicatesResult.status === "rejected") {
        const reason = duplicatesResult.reason;
        return writeEntry(userId, {
            loading: false,
            error: String(reason?.message || reason),
            seed,
            neighbours: []
        });
    }

    const neighbours = [...(duplicatesResult.value || [])]
        .filter((n) => !seed || n.id !== seed.id)
        .sort((a, b) => (b.createdTimestamp ?? 0) - (a.createdTimestamp ?? 0));
    return writeEntry(userId, {loading: false, error: null, seed, neighbours});
};

// Sweep every cached cluster, not just the open one: the account just
// deactivated is very likely a neighbour under other seeds too — and it is the
// seed of its own cluster.
const markDeactivated = (deactivatedId) => {
    const deactivate = (user) =>
        user?.id === deactivatedId ? {...user, approvalStatus: "DEACTIVATED"} : user;

    cache.forEach((entry, userId) => {
        const affectsSeed = entry.seed?.id === deactivatedId;
        const affectsNeighbours = entry.neighbours?.some((n) => n.id === deactivatedId);
        if (!affectsSeed && !affectsNeighbours) return;
        cache.set(userId, {
            ...entry,
            seed: deactivate(entry.seed),
            neighbours: entry.neighbours?.map(deactivate)
        });
    });
    notify();
};

// moment(undefined) is *now*, which would render every member with no
// birthdate as "(0)".
const getAge = (birthdate) => {
    if (!birthdate) return null;
    const age = moment().diff(moment(birthdate), "years");
    return Number.isFinite(age) ? age : null;
};

// Copied verbatim from Members.jsx:62-135 — Members.jsx is deliberately left
// untouched. Members' extra "AUTO APPROVED" tag is omitted: it reads
// user.approverId, which UserDataDto does not declare.
const statusTemplate = (user) => {
    switch (user.approvalStatus) {
        case "NEW_REGISTERED_ACCOUNT":
            if (!user.emailVerified) {
                return (
                    <div align={"center"}>
                        <Tag style={{width: "100%"}} icon={<ExclamationCircleOutlined/>} color="warning">
                            Unverified
                        </Tag>
                    </div>
                );
            } else {
                return (
                    <div align={"center"}>
                        <Tag style={{width: "100%"}} icon={<ExclamationCircleOutlined/>} color="default">
                            Tajneed missing
                        </Tag>
                    </div>
                );
            }
        case "APPROVAL_RQUESTED":
            return (
                <div align={"center"}>
                    <Tag style={{width: "100%"}} icon={<ClockCircleOutlined/>} color="processing">
                        {getUserStatusLabel(user)}
                    </Tag>
                </div>
            );
        case "APPROVED":
            return (
                <div align={"center"}>
                    <Tag style={{width: "100%"}} icon={<CheckCircleOutlined/>} color="success">
                        {getUserStatusLabel(user)}
                    </Tag>
                </div>
            );
        case "REJECTED":
        case "DEACTIVATED":
            return (
                <div align={"center"}>
                    <Tag style={{width: "100%"}} icon={<MinusCircleOutlined/>} color="error">
                        {getUserStatusLabel(user)}
                    </Tag>
                </div>
            );
        default:
            return "N/A";
    }
};

// The inner table declares its own eight columns; it shares nothing with the
// four columns of the summary table it is nested inside.
const buildColumns = (busyId, onDeactivate) => [
    {
        title: "Member",
        dataIndex: "",
        render: (user) => {
            const age = getAge(user.birthdate);
            return (
                <Avatar.Group>
                    <div style={{marginRight: "3px"}}>
                        <Avatar
                            src={
                                user.gender === UserDataDtoGenderEnum.Male
                                    ? gender_male2_icon
                                    : user.gender === UserDataDtoGenderEnum.Female
                                        ? gender_female_icon
                                        : gender_unknown_icon
                            }
                            shape="square"
                            size="large"
                        />
                    </div>
                    <div className="avatar-info">
                        <Title level={5}>
                            {user.firstName + " " + user.lastName}
                            {age !== null ? ` (${age})` : ""}
                        </Title>
                        <p>{user.email}</p>
                    </div>
                </Avatar.Group>
            );
        }
    },
    {
        title: "Urdu Name",
        dataIndex: "",
        render: (user) => (
            <div className="author-info">
                <p>{user.urduName || " - "}</p>
            </div>
        )
    },
    {
        title: "Jamaat",
        dataIndex: "",
        render: (user) => (
            <div className="author-info">
                <p>{user.aimsId}</p>
                <p>
                    <span>
                        {user?.jamaat ? user.jamaat : ""}
                        <br/>
                        {user?.country ? user.country.defaultLabel : ""}&nbsp;
                        {getCustomUnicodeFlagIcon(user?.country?.values?.flag)}
                    </span>
                </p>
            </div>
        )
    },
    {
        title: "Phone",
        dataIndex: "",
        render: (user) => (
            <div className="author-info">
                <p>{user.contactNumber || " - "}</p>
            </div>
        )
    },
    {
        title: "Address",
        dataIndex: "",
        render: (user) => (
            <div className="author-info">
                <p>{user.streetLine1}</p>
                <p>{user.streetLine2}</p>
                <p>
                    {user.zipCode} {user.city}{" "}
                    {user.state ? "(" + user.state + ")" : ""}
                </p>
            </div>
        )
    },
    {
        title: "Date of Birth",
        dataIndex: "",
        sorter: (a, b) => (a.birthdate || "").localeCompare(b.birthdate || ""),
        render: (user) => (
            <div className="author-info">
                <p>{user.birthdate ? moment(user.birthdate).format("DD.MM.yyyy") : " "}</p>
            </div>
        )
    },
    {
        title: "Status",
        dataIndex: "",
        // statusTemplate takes the user object, not the status string.
        render: (user) => statusTemplate(user)
    },
    {
        title: "Action",
        dataIndex: "",
        align: "right",
        render: (user) =>
            user.approvalStatus === "DEACTIVATED" ? (
                <div align={"center"}>—</div>
            ) : (
                <Button
                    style={{width: "100%"}}
                    type="default"
                    className="ant-tag-error"
                    loading={busyId === user.id}
                >
                    <Popconfirm
                        title="Are you sure you want to deactivate this User? You can reactivate this User later."
                        onConfirm={() => onDeactivate(user)}
                        okText="Yes"
                        cancelText="No"
                    >
                        Deactivate
                    </Popconfirm>
                </Button>
            )
    }
];

// One expansion of the Duplicate Accounts summary table: the account being
// examined on top, then the accounts that collide with it. Fetches its own data
// on mount.
function DuplicateMembersRow({userId}) {
    const entry = useSyncExternalStore(subscribe, () => cache.get(userId));
    const [busyId, setBusyId] = useState(null);

    useEffect(() => {
        if (!cache.has(userId)) fetchCluster(userId);
    }, [userId]);

    const handleDeactivate = async (user) => {
        const key = user.id;
        setBusyId(key);
        message.loading({content: "Deactivating User Account...", key});
        try {
            // Exact {users: [...]} wrapper — the service unwraps data.users
            // (acc-rest.service.jsx:34) before calling disableUsers.
            await accountRestService.deactivateUserAccounts({
                users: [{
                    id: user.id,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    email: user.email
                }]
            });
            message.success({content: "User Account deactivated", key, duration: 5});
            markDeactivated(key);
        } catch (error) {
            message.error({
                content: "Deactivating User Account failed: " + error,
                key,
                duration: 10
            });
        } finally {
            setBusyId(null);
        }
    };

    if (!entry || entry.loading) {
        return <Skeleton active title={false} paragraph={{rows: 3}}/>;
    }

    // Inline, not a toast: one failed row should not queue a page-wide message.
    if (entry.error) {
        return (
            <Alert
                type="error"
                showIcon
                message="Could not load the duplicates for this account."
                description={entry.error}
                action={
                    <Space>
                        <Button size="small" onClick={() => fetchCluster(userId)}>
                            Retry
                        </Button>
                    </Space>
                }
            />
        );
    }

    // The account being examined sits on top, flagged so the Member column can
    // mark it and the CSS can highlight the row.
    const dataSource = [
        ...(entry.seed ? [{...entry.seed, __isSeed: true}] : []),
        ...(entry.neighbours || [])
    ];

    if (!dataSource.length) {
        return <Alert type="info" showIcon message="No other accounts currently match this one."/>;
    }

    return (
        <>
            {entry.neighbours?.length ? null : (
                <Alert
                    type="info"
                    showIcon
                    style={{marginBottom: 12}}
                    message="No other accounts currently match this one."
                />
            )}
            <Table
                columns={buildColumns(busyId, handleDeactivate)}
                dataSource={dataSource}
                rowKey={(user) => user.id}
                rowClassName={(user) => (user.__isSeed ? "duplicate-seed-row" : "")}
                pagination={false}
                size="small"
                className="ant-border-space"
            />
        </>
    );
}

export default memo(DuplicateMembersRow);
